import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutDashboard, FileInput, FileOutput, ClipboardList, CreditCard, Users, Mail, Building2, LogOut, ChevronDown, Sun, Moon, Shield } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/accounts-payable', icon: FileInput, label: 'Accounts Payable' },
  { to: '/accounts-receivable', icon: FileOutput, label: 'Accounts Receivable' },
  { to: '/purchase-orders', icon: ClipboardList, label: 'Purchase Orders' },
  { to: '/payments', icon: CreditCard, label: 'Payments' },
  { to: '/contacts', icon: Users, label: 'Clients & Vendors' },
  { to: '/company-settings', icon: Building2, label: 'Company Profile' },
  { to: '/email-settings', icon: Mail, label: 'Email Settings' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 border-r flex flex-col" style={{ background: `hsl(var(--sidebar-bg))`, borderColor: `hsl(var(--sidebar-border))` }}>
        <div className="p-5 border-b" style={{ borderColor: `hsl(var(--sidebar-border))` }}>
          <h1 className="text-lg font-bold tracking-tight text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            GO MO Group
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5 tracking-wide uppercase">Digital Business Solutions</p>
        </div>
        <nav className="flex-1 p-3 space-y-0.5" data-testid="sidebar-nav">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid="nav-admin"
            >
              <Shield size={18} />
              <span>Admin Panel</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: `hsl(var(--sidebar-border))` }}>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-3">v1.0.0</p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b flex items-center justify-between px-6 flex-shrink-0" style={{ background: `hsl(var(--header-bg))` }}>
          <div />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground hover:text-foreground" data-testid="theme-toggle-button">
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground" data-testid="user-menu-trigger">
                  <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center text-xs font-semibold text-white">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm">{user?.name || 'User'}</span>
                  <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-muted-foreground focus:bg-transparent cursor-default">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-400 cursor-pointer" data-testid="logout-button">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6 bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
